// Переписки с соискателями. Одна строка — один диалог (правило 2): кто, когда писал в
// последний раз, что в нём ждёт человека и в каком он состоянии.
//
// ## Почему заголовок строки — «Соискатель», а не имя
//
// Имени соискателя сервер в списке переписок не отдаёт вовсе: в ответе есть только
// `external_chat_id` — опознаватель чата на площадке. Ставить его в заголовок нельзя, это
// служебное обозначение, человеку оно не говорит ничего (`.claude/rules/ui-views.md`).
// Поэтому заголовок — «Соискатель» плюс момент последнего сообщения: без имени различает
// строки между собой ровно время, и в списке, отсортированном по нему, человек ищет «тот,
// что писал в 14:32». Сам `external_chat_id` живёт в шапке карточки, где он и нужен — при
// разборе с площадкой.
//
// ## Ради чего этот список открывают
//
// Ради двух чисел: сколько предложенных помощником ответов ждут решения и есть ли открытая
// передача человеку. Оба стоят в подписи ПЕРЕД учётной записью — обрезаться первым обязано
// название записи, а не работа, которая ждёт.
//
// Правило 11: при смене отбора строки остаются на месте (keepPreviousData в queries.ts),
// скелет рисуется только тогда, когда данных нет даже в кэше прошлой сессии.

import { RiChat3Line } from '@remixicon/react';
import { useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { ListPane } from '@/layout/ListPane';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Hint from '@/shared/ui/hint';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';

import type { DialogRow, DialogStatus } from '../api/dialogs';
import { DIALOG_STATUSES } from '../api/dialogs';
import { DIALOG_FORMS, DIALOG_STATUS_TITLES } from '../dictionaries';
import { useAccountsQuery, useDialogsQuery } from '../queries';
import type { HiringRoute } from '../route';
import { FILTER_ACCOUNT, FILTER_STATUS, hiringHref, setFilter } from '../route';

/**
 * Оттенок метки состояния. Цвет ДУБЛИРУЕТ слово, а не заменяет его: название приезжает с
 * сервера полем `status_title`, здесь только тон.
 *
 * Живёт рядом со списком, потому что список — единственное место, где меток шесть штук
 * подряд и разница между ними читается глазом. Карточка берёт его отсюда же: вторая копия
 * разошлась бы на первой правке, и одно состояние оказалось бы двух цветов на соседних
 * поверхностях.
 */
export const DIALOG_STATUS_COLORS: Readonly<
  Record<DialogStatus, 'gray' | 'blue' | 'green' | 'orange' | 'purple'>
> = {
  awaiting_first_reply: 'gray',
  assistant: 'blue',
  waiting_operator: 'orange',
  operator: 'purple',
  paused: 'orange',
  finished: 'green',
};

export function dialogStatusColor(status: string): 'gray' | 'blue' | 'green' | 'orange' | 'purple' {
  return DIALOG_STATUS_COLORS[status as DialogStatus] ?? 'gray';
}

/** Формы для счётчика предложенных ответов в подписи строки. */
const ANSWER_FORMS = ['ответ', 'ответа', 'ответов'] as const;

/**
 * Когда в диалоге писали в последний раз. Сегодняшнее — часами, вчерашнее и старше — датой:
 * голое «09:15» у позавчерашней переписки человек прочтёт как сегодняшнее и решит, что
 * соискатель на связи.
 */
function когдаПисали(value: string | null, now: Date): string | null {
  const момент = parseMoment(value);
  if (момент === null) {
    return null;
  }
  return formatDateDMY(момент) === formatDateDMY(now)
    ? formatTimeHM(момент)
    : formatDateDMY(момент);
}

function заголовокСтроки(диалог: DialogRow, now: Date): string {
  const когда = когдаПисали(диалог.last_message_at, now);
  return когда === null ? 'Соискатель' : `Соискатель · ${когда}`;
}

function подписьСтроки(диалог: DialogRow): string {
  const части: string[] = [];
  if (диалог.ждут_подтверждения > 0) {
    части.push(
      диалог.ждут_подтверждения === 1
        ? 'ответ помощника ждёт решения'
        : `${countLabel(диалог.ждут_подтверждения, ANSWER_FORMS)} ждут решения`,
    );
  }
  // Открытая передача при состоянии «ждёт оператора» — это то же самое, сказанное меткой
  // справа. Одно и то же не пишется дважды в одной строке, а место в подписи стоит дорого:
  // за ним следом идёт название учётной записи.
  if (диалог.открытых_передач > 0 && диалог.status !== 'waiting_operator') {
    части.push('передан человеку');
  }
  части.push(диалог.account_title);
  return части.join(' · ');
}

export interface DialogsListProps {
  route: HiringRoute;
}

export function DialogsList({ route }: DialogsListProps) {
  const navigate = useNavigate();
  const { view, search, rowId } = route;

  const status = search.filters[FILTER_STATUS];
  const accountId = search.filters[FILTER_ACCOUNT];

  const query = useDialogsQuery({ status, account_id: accountId });
  const accounts = useAccountsQuery();

  // `replace`: подбор отбора — это один шаг в истории браузера, а не двадцать.
  function apply(next: SearchState) {
    void navigate(hiringHref(view, next, rowId), { replace: true });
  }

  const narrowed = status !== undefined || accountId !== undefined;
  const now = new Date();

  const filters = (
    <>
      <SelectField
        id='hiring-dialog-status'
        label='Состояние переписки'
        value={status ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_STATUS, value === NO_VALUE ? undefined : value))
        }
        options={DIALOG_STATUSES.map((code) => ({
          value: code,
          label: DIALOG_STATUS_TITLES[code],
        }))}
        noneLabel='Любое состояние'
        placeholder='Любое состояние'
        hint={
          status === 'waiting_operator'
            ? 'Помощник передал эти переписки человеку и сам в них больше не пишет.'
            : undefined
        }
      />

      <SelectField
        id='hiring-dialog-account'
        label='Учётная запись площадки'
        value={accountId ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_ACCOUNT, value === NO_VALUE ? undefined : value))
        }
        options={(accounts.data?.items ?? []).map((account) => ({
          value: account.id,
          label: account.title,
        }))}
        noneLabel='Все записи'
        placeholder='Все записи'
        pending={accounts.isPending}
        failed={accounts.isError}
        failedError={accounts.error as ApiError | null}
        emptyNote='Учётных записей площадки нет: переписке неоткуда взяться, пока не заведена хотя бы одна.'
      />
    </>
  );

  return (
    <ListPane
      caption={query.data === undefined ? 'Переписка' : countLabel(query.data.total, DIALOG_FORMS)}
      sliceAt={parseMoment(query.data?.generated_at)}
      filters={filters}
      appliedFilters={[
        status === undefined ? null : 'состояние',
        accountId === undefined ? null : 'учётная запись',
      ].filter((имя): имя is string => имя !== null)}
      width='имена'
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='список переписок с соискателями'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiChat3Line}
            title={narrowed ? 'Под этот отбор переписок нет' : 'Переписок пока нет'}
            illustration={narrowed ? undefined : 'переписка'}
            description={
              narrowed
                ? 'Список пуст из-за наложенного отбора, а не потому, что переписок нет вовсе. Снимите состояние и учётную запись — и увидите все.'
                : 'Переписка заводится сама, когда соискатель откликается на вакансию на площадке: первое его сообщение и создаёт диалог. Руками их не заводят.'
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((диалог) => (
              <ListRow
                key={диалог.id}
                to={hiringHref(view, search, диалог.id)}
                selected={диалог.id === rowId}
                title={заголовокСтроки(диалог, now)}
                subtitle={подписьСтроки(диалог)}
                badge={
                  <Badge.Root
                    variant='lighter'
                    color={dialogStatusColor(диалог.status)}
                    size='medium'
                  >
                    {диалог.status_title}
                  </Badge.Root>
                }
              />
            ))}

            {data.items.length < data.total && (
              <div className='px-4 py-2'>
                <Hint.Root>
                  Показаны первые {data.items.length} из {data.total}. Сузьте отбор — переписка,
                  которую вы ищете, может остаться за пределами страницы.
                </Hint.Root>
              </div>
            )}
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}
